import React, { useState } from 'react';
import { X, Star } from 'lucide-react';
import { useTranslation } from '../hooks/useTranslation';

interface RouteFeedbackModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (feedback: RouteFeedback) => void;
  onSkip: () => void;
  routeInfo: {
    startAddress: string;
    endAddress: string;
    date: string;
  };
}

interface RouteFeedback {
  rating: number;
  selectedTags: string[];
}

const RouteFeedbackModal: React.FC<RouteFeedbackModalProps> = ({
  isOpen,
  onClose,
  onSave,
  onSkip,
  routeInfo
}) => {
  const { t } = useTranslation();
  const [rating, setRating] = useState(0);
  const [selectedTags, setSelectedTags] = useState<string[]>([]);

  const FEEDBACK_TAGS = [
    t('feedback.tag.busy'),
    t('feedback.tag.parkedBikes'),
    t('feedback.tag.fewRestPlaces'),
    t('feedback.tag.narrowSidewalk'),
    t('feedback.tag.manyCrossings')
  ];

  const handleStarClick = (starRating: number) => {
    setRating(starRating);
  };

  const handleTagToggle = (tag: string) => {
    setSelectedTags(prev => 
      prev.includes(tag) 
        ? prev.filter(t => t !== tag)
        : [...prev, tag]
    );
  };

  const handleSave = () => {
    const feedback: RouteFeedback = {
      rating,
      selectedTags,
    };
    onSave(feedback);
    onClose();
  };

  const handleSkip = () => {
    onSkip();
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="feedback-modal-overlay">
      <div className="feedback-modal">
        {/* Header */}
        <div className="feedback-header">
          <h2 className="feedback-title">{t('feedback.title')}</h2>
          <button className="feedback-close-btn" onClick={onClose}>
            <X size={20} />
          </button>
        </div>

        {/* Route Info */}
        <div className="feedback-route-info">
          <p>{t('feedback.routeInfo', { start: routeInfo.startAddress, end: routeInfo.endAddress, date: routeInfo.date })}</p>
        </div>

        {/* Star Rating */}
        <div className="feedback-rating">
          <div className="star-rating">
            {[1, 2, 3, 4, 5].map((star) => (
              <button
                key={star}
                className={`star-btn ${star <= rating ? 'filled' : ''}`}
                onClick={() => handleStarClick(star)}
                type="button"
              >
                <Star 
                  size={32} 
                  fill={star <= rating ? '#000' : 'none'} 
                  stroke="#000"
                />
              </button>
            ))}
          </div>
        </div>

        {/* Additional Feedback Question */}
        <div className="feedback-additional">
          <h3 className="feedback-question">{t('feedback.additionalQuestion')}</h3>
          
          {/* Feedback Tags */}
          <div className="feedback-tags">
            {FEEDBACK_TAGS.map((tag) => (
              <button
                key={tag}
                className={`feedback-tag ${selectedTags.includes(tag) ? 'selected' : ''}`}
                onClick={() => handleTagToggle(tag)}
                type="button"
              >
                {tag}
              </button>
            ))}
          </div>
        </div>

        {/* Action Buttons */}
        <div className="feedback-actions">
          <button 
            className="feedback-save-btn"
            onClick={handleSave}
            disabled={rating === 0}
          >
            {t('button.save')}
          </button>
          <button 
            className="feedback-skip-btn"
            onClick={handleSkip}
          >
            {t('feedback.skip')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default RouteFeedbackModal;
